import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field, Input, Textarea, SaveBar } from "./adminUi";
import ImageUpload from "../../components/ImageUpload";

export default function AdminHome() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const update = (k, v) =>
    setContent((c) => ({ ...c, hero: { ...c.hero, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Hero section updated");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="text-white/60">Loading…</div>;

  const h = content.hero;
  return (
    <div data-testid="admin-home">
      <p className="text-xs uppercase tracking-[0.25em] text-fuchsia-400">Home page</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-white md:text-4xl">Branding & Hero</h1>
      <p className="mt-3 text-white/60">Edit your logo and the top of your home page.</p>

      <div className="mt-10 rounded-2xl border border-white/10 bg-[#131B2F] p-8">
        <h2 className="text-lg font-semibold text-white">Logo</h2>
        <p className="mt-1 text-sm text-white/50">
          Upload an image file or paste a URL. PNG/SVG with transparent background works best on dark navy.
          Changes apply site-wide.
        </p>
        <div className="mt-5">
          <ImageUpload
            value={content.logo_url || ""}
            onChange={(url) => setContent((c) => ({ ...c, logo_url: url }))}
            label=""
            aspect="square"
            testId="logo-upload"
          />
        </div>
      </div>

      <div className="mt-8 grid gap-6 rounded-2xl border border-white/10 bg-[#131B2F] p-8">
        <h2 className="text-lg font-semibold text-white">Hero section</h2>
        <Field label="Eyebrow">
          <Input value={h.eyebrow} onChange={(e) => update("eyebrow", e.target.value)} data-testid="home-eyebrow-input" />
        </Field>
        <Field label="Headline" hint="The last word is automatically styled with the brand gradient on the site.">
          <Input value={h.headline} onChange={(e) => update("headline", e.target.value)} data-testid="home-headline-input" />
        </Field>
        <Field label="Subheadline">
          <Textarea rows={3} value={h.subheadline} onChange={(e) => update("subheadline", e.target.value)} data-testid="home-subheadline-input" />
        </Field>
        <div className="grid gap-6 md:grid-cols-2">
          <Field label="Primary button label">
            <Input value={h.primary_cta_label} onChange={(e) => update("primary_cta_label", e.target.value)} data-testid="home-primary-label" />
          </Field>
          <Field label="Primary button link">
            <Input value={h.primary_cta_href} onChange={(e) => update("primary_cta_href", e.target.value)} data-testid="home-primary-href" />
          </Field>
          <Field label="Secondary button label">
            <Input value={h.secondary_cta_label} onChange={(e) => update("secondary_cta_label", e.target.value)} data-testid="home-secondary-label" />
          </Field>
          <Field label="Secondary button link">
            <Input value={h.secondary_cta_href} onChange={(e) => update("secondary_cta_href", e.target.value)} data-testid="home-secondary-href" />
          </Field>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#131B2F] p-8">
        <h2 className="text-lg font-semibold text-white">Favicon</h2>
        <p className="mt-1 text-sm text-white/50">
          The small icon shown in browser tabs and bookmarks. A 32×32 or 64×64 PNG works best.
          If none is set, the browser default is used.
        </p>
        <div className="mt-5">
          <ImageUpload
            value={content.favicon_url || ""}
            onChange={(url) => setContent((c) => ({ ...c, favicon_url: url }))}
            label=""
            aspect="square"
            testId="favicon-upload"
          />
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#131B2F] p-8" data-testid="clients-editor">
        <h2 className="text-lg font-semibold text-white">Clients strip (home page marquee)</h2>
        <p className="mt-1 text-sm text-white/50">
          These names scroll across the top of your home page. Separate them with <span className="text-white">commas (,)</span>,{" "}
          <span className="text-white">periods (.)</span>, or <span className="text-white">new lines</span>. Leave empty to hide the strip.
        </p>
        <Field label="Client names">
          <Textarea
            rows={3}
            value={(content.clients || []).join(", ")}
            onChange={(e) => {
              const names = e.target.value
                .split(/[.,\n]/)
                .map((x) => x.trim())
                .filter(Boolean);
              setContent((c) => ({ ...c, clients: names }));
            }}
            placeholder="Nova, Maple, Hearth, Atlas, Orbit"
            data-testid="clients-input"
          />
        </Field>
        {(content.clients || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {content.clients.map((c, i) => (
              <span key={i} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80">
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      <SaveBar onSave={save} saving={saving}>Changes are published live when saved.</SaveBar>
    </div>
  );
}
